import * as callService from '@/services/call.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type {
  Call,
  CallDetailResponse,
  CallFilterParams,
  CallTranscript,
  ConnectionDetails,
} from '@/types/call.type';
import type { PaginatedResponse, QueryOptions } from '@/types/common.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useCallsQuery = (
  params: CallFilterParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Call>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.calls.list(
      params.page || 1,
      params.page_size,
      params.query || '',
      params.filters
    ),
    queryFn: () => callService.fetchAllCalls(params),
    ...options,
  });
};

export const useDispatchAgentWebMutation = (options?: QueryOptions<ConnectionDetails>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.calls.mutations.dispatchWeb,
    mutationFn: callService.dispatchAgentWeb,
    onSuccess: connectionDetails => {
      notifyMutationSuccessWithOptions(
        options,
        'Web call started',
        'Agent dispatch was started successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.calls.all });
      options?.onSuccess?.(connectionDetails);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Web call dispatch failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDispatchAgentPhoneMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.calls.mutations.dispatchPhone,
    mutationFn: callService.dispatchAgentPhone,
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Phone call started',
        'Phone dispatch was started successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.calls.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Phone call dispatch failed', error);
      options?.onError?.(error);
    },
  });
};
export const useCallDetailQuery = (
  callId: number,

  options?: Omit<UseQueryOptions<CallDetailResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.calls.detail(callId),
    queryFn: () => callService.fetchCallById(callId),
    ...options,
  });
};

/**
 * Fetch call transcripts query
 */
export const useCallTranscriptsQuery = (
  callId: number,
  options?: Omit<UseQueryOptions<CallTranscript[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.calls.transcripts(callId),
    queryFn: () => callService.fetchCallTranscripts(callId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch call recording query
 */
export const useCallRecordingQuery = (
  callId: number,
  options?: Omit<UseQueryOptions<string | null, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.calls.recording(callId),
    queryFn: () => callService.fetchCallRecording(callId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};
